import React from "react";
import { Controller, useForm } from "react-hook-form";
import { Button } from "primereact/button";
import { classNames } from "primereact/utils";
import { Dialog } from "primereact/dialog";
import { ConfirmDialog } from "primereact/confirmdialog";
import { InputTextarea } from "primereact/inputtextarea";
import { Dropdown } from "primereact/dropdown";
import { MultiSelect } from "primereact/multiselect";
import { InputText } from "primereact/inputtext";
import { Toast } from "primereact/toast";
import { addSchool, editSchool } from "../../../apis/School";
import utils from "../../../utils";
import constants from "../../../constants";
import { useAuthStore } from "../../../store/AuthStore";
import {
  getCategories,
  getCountries,
  getSchoolTypes,
  getStates,
  getCities,
} from "../../../apis/Utilities";
import useFetchSchoolDetails from "../../hooks/useFetchSchoolDetails";

const AddEditSchool = ({ schoolId, onEdit, isCreateSchool , setIsCreateSchool }) => {

  const { schoolDetail } = useFetchSchoolDetails(schoolId);

  let isEdit = (schoolId)?true:false;

  const defaultValues = {
    foundyear: "",
    category: null,
    schooltype: null,
    website: "",
    email: "",
    phonenumber: "",
    city: null,
    state: null,
    country: null,
    description: "",
    motto: "",
    name: "",
  };
  
  const {
    control,
    formState: { errors },
    handleSubmit,
    getValues,
    setValue,
    reset,
  } = useForm({ defaultValues });

  const [loading, setLoading] = React.useState(false);
  const toast = React.useRef(null);
  const [countries, setCountries] = React.useState([]);
  const [selectedCountry, setSelectedCountry] = React.useState(null);
  const [states, setStates] = React.useState([]);
  const [selectedCity, setSelectedCity] = React.useState(null);
  const [cities, setCities] = React.useState([]);
  const [selectedState, setSelectedState] = React.useState(null);
  const [selectedSchoolType, setSelectedSchoolType] = React.useState(null);
  const [schoolTypes, setSchoolTypes] = React.useState([]);
  const [selectedCategories, setSelectedCategories] = React.useState([]);
  const [categories, setCategories] = React.useState([]);
  const [{ id, username }] = useAuthStore();
  

  async function fetchCountries() {
    let countriesData = await getCountries();
    setCountries(countriesData.data);
    return countriesData.data;
  }

  async function fetchSchoolTypes() {
    let schoolTypesData = await getSchoolTypes();
    setSchoolTypes(schoolTypesData.data);
    return schoolTypesData.data;
  }

  async function fetchCategories() {
    let categoriesData = await getCategories();
    setCategories(categoriesData.data);
    return categoriesData.data;
  }

  async function fetchStates(countryId) {
    let statesData = await getStates(countryId);
    setStates(statesData.data);
    return statesData.data;
  }

  async function fetchCities(stateId) {
    let citiesData = await getCities(stateId);
    setCities(citiesData.data);
    return citiesData.data;
  }

  React.useEffect(() => {
    try {
      fetchCountries();
      fetchSchoolTypes();
      fetchCategories();
    } catch (error) {
      console.log(`Getting drop downs error: ${error}`);
      throw error;
    }
  }, []);

  React.useEffect(() => {
    try {
      if (selectedCountry) {
        fetchStates(selectedCountry.id);
      }
    } catch (error) {
      console.log(`Setting states error: ${error}`);
      throw error;
    }
  }, [selectedCountry]);

  React.useEffect(() => {
    try {
      if (selectedState) {
        fetchCities(selectedState.id);
      }
    } catch (error) {
      console.log(`Setting cities error: ${error}`);
      throw error;
    }
  }, [selectedState]);

  React.useEffect(() => {
    
    try {

      if (isEdit === true) {

          if(schoolDetail)
          {
            setValue("foundyear", schoolDetail.foundYear);
            setValue("name", schoolDetail.name);
            setValue("phonenumber", schoolDetail.phoneNumber);
            setValue("email", schoolDetail.email);
            setValue("motto", schoolDetail.motto);
            setValue("description", schoolDetail.description);
            setValue("website", schoolDetail.website);

            fetchCategories().then(cg => {
              setCategories(cg);
              let catG = [];
              schoolDetail.categories.forEach(element => {
                catG.push(cg.find(f => f.id === element));
              });
              setSelectedCategories(catG);
              setValue("category", catG);
            });
            
            fetchSchoolTypes().then(sp => {
              setSchoolTypes(sp);
              let schTy = sp.find(f => f.id === schoolDetail.schoolTypeId)
              setSelectedSchoolType(schTy);
              setValue("schooltype", schTy);
            });

            
            
            fetchCountries().then(fc => { 
              setCountries(fc);
              let count = countries.find(f => f.id === schoolDetail.countryId);
              setSelectedCountry(count);
              setValue("country", count);
              if(count)
              {
                fetchStates(count.id).then(st => { 
                  setStates(st); 
                  let sta = st.find(p => p.id === schoolDetail.stateId);
                  setSelectedState(sta);
                  setValue("state", sta);
                  if(sta)
                  { 
                    fetchCities(sta.id).then(ct => {
                      setCities(ct);
                      let cit = ct.find(q => q.id === schoolDetail.cityId);
                      setSelectedCity(cit);
                      setValue("city", cit);
                    })
                  }
                });
              }
            });
          }
      }
    } catch (error) {
      console.log(`Setting states error: ${error}`);
      throw error;
    }

  },[schoolDetail]);

  const getFormErrorMessage = (name) => {
    return errors[name] ? (
      <small className="p-error">{errors[name].message}</small>
    ) : (
      <small className="p-error">&nbsp;</small>
    );
  };

  const onSubmit = async (data) => {

    if (loading) return;

    setLoading(true);

    const {
      name,
      description,
      motto,
      city,
      state,
      country,
      foundyear,
      phonenumber,
      email,
      website,
      schooltype,
      category,
    } = data;

    try {

      let rdescription;

      if (!schoolId) {
        const { resp, responseCodeDescription } = await addSchool({
          name: name,
          description: description,
          motto: motto,
          cityId: city.id,
          stateId: state.id,
          countryId: country.id,
          ownerId: id,
          foundYear: foundyear,
          phoneNumber: phonenumber,
          email: email,
          website: website,
          schoolTypeId: schooltype.id,
          createdBy: username,
          categories: category.map((e) => e.id)
        });

        rdescription = responseCodeDescription;

      } else {

        const { data: responseCodeDescription } = await editSchool({
          id: schoolId,
          name: name,
          description: description,
          motto: motto,
          cityId: city.id,
          stateId: state.id,
          countryId: country.id,
          foundYear: foundyear,
          phoneNumber: phonenumber,
          email: email,
          website: website,
          schoolTypeId: schooltype.id,
          modifiedBy: username,
          categories: category.map((e) => e.id),
        });

        rdescription = responseCodeDescription;
       
        onEdit();

      }

      toast.current.show(
        utils.toastCallback({
          severity: "success",
          summary: "Success",
          detail: rdescription,
        })
      );

      reset();

      setLoading(false);

      setIsCreateSchool(false);

    } catch (error) {
      setLoading(false);

      if (error.response) {
        const {
          data: { responseCodeDescription }
        } = error.response;

        toast.current.show(
          utils.toastCallback({
            severity: "error",
            summary: "Save Error",
            detail: responseCodeDescription,
          })
        );
      } else {
        toast.current.show(
          utils.toastCallback({
            severity: "error",
            summary: "Network Error",
            detail: constants.networkErrorMessage,
          })
        );
      }
    }
  };

  return (
    <div>

<ConfirmDialog />    <Dialog
    header={!schoolId ? "Create School" : "Edit School"}
    visible={isCreateSchool}
    style={{ width: "50vw" }}
    onHide={() => setIsCreateSchool(false)}
  >
      <div className="card">
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="flex flex-wrap gap-3 mt-5">
            <div className="flex-auto">
              <Controller
                name="name"
                control={control}
                rules={{ required: "Name is required." }}
                render={(props) => (
                  <>
                    <label
                      htmlFor={props.name}
                      className={classNames({ "p-error": errors.value })}
                    ></label>
                    <span className="p-float-label">
                      <InputText
                        id={props.name}
                        value={props.value}
                        className="w-full"
                        onChange={(e) => props.onChange(e.target.value)}
                      />
                      <label htmlFor={props.name}>Name</label>
                    </span>
                    {getFormErrorMessage(props.name)}
                  </>
                )}
              />
            </div>

            <div className="flex-auto">
              <Controller
                name="motto"
                control={control}
                rules={{ required: "Motto is required." }}
                render={(props) => (
                  <>
                    <label
                      htmlFor={props.name}
                      className={classNames({ "p-error": errors.value })}
                    ></label>
                    <span className="p-float-label">
                      <InputText
                        id={props.name}
                        value={props.value}
                        className="w-full"
                        onChange={(e) => props.onChange(e.target.value)}
                      />
                      <label htmlFor={props.name}>Motto</label>
                    </span>
                    {getFormErrorMessage(props.name)}
                  </>
                )}
              />
            </div>
          </div>

          <div className="flex flex-wrap gap-3 mt-5">
            <div className="flex-auto">
              <Controller
                name="description"
                control={control}
                rules={{ required: "Description is required." }}
                render={(props) => (
                  <>
                    <label
                      htmlFor={props.name}
                      className={classNames({ "p-error": errors.value })}
                    ></label>
                    <span className="p-float-label">
                      <InputTextarea
                        id={props.name}
                        value={props.value}
                        className="w-full"
                        onChange={(e) => props.onChange(e.target.value)}
                      />
                      <label htmlFor={props.name}>Description</label>
                    </span>
                    {getFormErrorMessage(props.name)}
                  </>
                )}
              />
            </div>
          </div>

          <div className="flex flex-wrap gap-3 mt-5">
            <div className="flex-auto">
              <Controller
                name="country"
                control={control}
                rules={{ required: "Country is required." }}
                render={(props) => (
                  <>
                    <label
                      htmlFor={props.name}
                      className={classNames({ "p-error": errors.value })}
                    ></label>
                    <span className="p-float-label">
                      <Dropdown
                        id={props.name}
                        value={props.value}
                        options={countries}
                        className="w-full"
                        onChange={(e) => {
                          props.onChange(e.target.value);
                          setSelectedCountry(e.target.value);
                        }}
                        optionLabel="name"
                        filter
                      />
                      <label htmlFor={props.name}>Country</label>
                    </span>
                    {getFormErrorMessage(props.name)}
                  </>
                )}
              />
            </div>

            <div className="flex-auto">
              <Controller
                name="state"
                control={control}
                rules={{ required: "State is required." }}
                render={(props) => (
                  <>
                    <label
                      htmlFor={props.name}
                      className={classNames({ "p-error": errors.value })}
                    ></label>
                    <span className="p-float-label">
                      <Dropdown
                        id={props.name}
                        value={props.value}
                        options={states}
                        className="w-full"
                        onChange={(e) => {
                          props.onChange(e.target.value);
                          setSelectedState(e.target.value);
                        }}
                        optionLabel="name"
                        filter
                      />
                      <label htmlFor={props.name}>State</label>
                    </span>
                    {getFormErrorMessage(props.name)}
                  </>
                )}
              />
            </div>

            <div className="flex-auto">
              <Controller
                name="city"
                control={control}
                rules={{ required: "City is required." }}
                render={(props) => (
                  <>
                    <label
                      htmlFor={props.name}
                      className={classNames({ "p-error": errors.value })}
                    ></label>
                    <span className="p-float-label">
                      <Dropdown
                        id={props.name}
                        value={props.value}
                        options={cities}
                        className="w-full"
                        onChange={(e) => {
                          props.onChange(e.target.value);
                          setSelectedCity(e.target.value);
                        }}
                        optionLabel="name"
                        filter
                      />
                      <label htmlFor={props.name}>City</label>
                    </span>
                    {getFormErrorMessage(props.name)}
                  </>
                )}
              />
            </div>
          </div>

          <div className="flex flex-wrap gap-3 mt-5">
            <div className="flex-auto">
              <Controller
                name="phonenumber"
                control={control}
                rules={{ required: "Phone Number is required." }}
                render={(props) => (
                  <>
                    <label
                      htmlFor={props.name}
                      className={classNames({ "p-error": errors.value })}
                    ></label>
                    <span className="p-float-label">
                      <InputText
                        id={props.name}
                        value={props.value}
                        className="w-full"
                        onChange={(e) => props.onChange(e.target.value)}
                      />
                      <label htmlFor={props.name}>Phone Number</label>
                    </span>
                    {getFormErrorMessage(props.name)}
                  </>
                )}
              />
            </div>

            <div className="flex-auto">
              <Controller
                name="email"
                control={control}
                rules={{ required: "Email is required." }}
                render={(props) => (
                  <>
                    <label
                      htmlFor={props.name}
                      className={classNames({ "p-error": errors.value })}
                    ></label>
                    <span className="p-float-label">
                      <InputText
                        id={props.name}
                        value={props.value}
                        className="w-full"
                        onChange={(e) => props.onChange(e.target.value)}
                      />
                      <label htmlFor={props.name}>Email</label>
                    </span>
                    {getFormErrorMessage(props.name)}
                  </>
                )}
              />
            </div>

            <div className="flex-auto">
              <Controller
                name="website"
                control={control}
                rules={{ required: "Website is required." }}
                render={(props) => (
                  <>
                    <label
                      htmlFor={props.name}
                      className={classNames({ "p-error": errors.value })}
                    ></label>
                    <span className="p-float-label">
                      <InputText
                        id={props.name}
                        value={props.value}
                        className="w-full"
                        onChange={(e) => props.onChange(e.target.value)}
                      />
                      <label htmlFor={props.name}>Website</label>
                    </span>
                    {getFormErrorMessage(props.name)}
                  </>
                )}
              />
            </div>
          </div>

          <div className="flex flex-wrap gap-3 mt-5">
            <div className="flex-auto">
              <Controller
                name="schooltype"
                control={control}
                rules={{ required: "School Type is required." }}
                render={(props) => (
                  <>
                    <label
                      htmlFor={props.name}
                      className={classNames({ "p-error": errors.value })}
                    ></label>
                    <span className="p-float-label">
                      <Dropdown
                        id={props.name}
                        value={props.value}
                        options={schoolTypes}
                        className="w-full"
                        onChange={(e) => {
                          props.onChange(e.target.value);
                          setSelectedSchoolType(e.target.value);
                        }}
                        optionLabel="name"
                        filter
                      />
                      <label htmlFor={props.name}>School Type</label>
                    </span>
                    {getFormErrorMessage(props.name)}
                  </>
                )}
              />
            </div>

            <div className="flex-auto">
              <Controller
                name="category"
                control={control}
                rules={{ required: "Catgory is required." }}
                render={(props) => (
                  <>
                    <label
                      htmlFor={props.name}
                      className={classNames({ "p-error": errors.value })}
                    ></label>
                    <span className="p-float-label">
                      <MultiSelect
                        id={props.name}
                        value={props.value}
                        options={categories}
                        className="w-full"
                        onChange={(e) => {
                          props.onChange(e.target.value);
                          setSelectedCategories(e.value);
                        }}
                        optionLabel="name"
                        filter
                      />
                      <label htmlFor={props.name}>Category</label>
                    </span>
                    {getFormErrorMessage(props.name)}
                  </>
                )}
              />
            </div>

            <div className="flex-auto">
              <Controller
                name="foundyear"
                control={control}
                rules={{ required: "Found Year is required." }}
                render={(props) => (
                  <>
                    <label
                      htmlFor={props.name}
                      className={classNames({ "p-error": errors.value })}
                    ></label>
                    <span className="p-float-label">
                      <InputText
                        id={props.name}
                        value={props.value}
                        className="w-full"
                        onChange={(e) => props.onChange(e.target.value)}
                        keyfilter={"num"}
                        maxLength={5}
                      />
                      <label htmlFor={props.name}>Found Year</label>
                    </span>
                    {getFormErrorMessage(props.name)}
                  </>
                )}
              /> 

            </div>
          </div>

          <div className="card flex justify-content-center mt-5">
            <Button
              label={!loading ? (!isEdit ? "Add" : "Edit") : null}
              type="submit"
              icon={
                loading
                  ? "pi pi-spin pi-spinner"
                  : !isEdit
                  ? "pi pi-plus"
                  : "pi pi-pencil"
              }
              rounded
            />
          </div>
        </form>

      <Toast ref={toast} />
      </div>
      </Dialog>
    </div>

  );
};

export default AddEditSchool;
